/**
 * NCR Detail — Section 3: Reject Details & Action.
 *
 * Extracted verbatim from NCRDetailTab for the 500-line file-size rule.
 * Includes fault group/category filtering, liability sub-block, disposition
 * details, containment status card and key dates.
 */

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  NonConformanceReport,
  UserProfile,
  SourceOfRejection,
  FaultCodeGroup,
  FaultCodeCategory,
} from '../../../lib/api';
import { NCREditFormData } from '../types';
import { Section } from '../Section';
import NCRContainmentStatusCard from '../../../components/quality/NCRContainmentStatusCard';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface RejectDetailsSectionProps {
  ncr: NonConformanceReport;
  formData: NCREditFormData;
  canEdit: boolean;
  customerUsers: UserProfile[];
  sourcesOfRejection: SourceOfRejection[];
  faultGroups: FaultCodeGroup[];
  faultCategories: FaultCodeCategory[];
  /** Customer review 2026-07: customer-side viewers may confirm containment
   *  even after the NCR has moved past its editable stage. */
  canConfirmContainment?: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
}

export function RejectDetailsSection({
  ncr,
  formData,
  canEdit,
  customerUsers,
  sourcesOfRejection,
  faultGroups,
  faultCategories,
  canConfirmContainment = false,
  onUpdateFormData,
}: RejectDetailsSectionProps) {
  const { formatDate } = useFormatters();
  // Filter categories by selected fault group
  const selectedFaultGroup = (formData.fault_group as number | undefined) ?? ncr.fault_group ?? null;
  const filteredCategories = selectedFaultGroup
    ? faultCategories.filter((c) => c.fault_group === selectedFaultGroup)
    : [];
  const liabilityType = (formData.liability_type ?? ncr.liability_type ?? 0) as number;
  const collectionRequired = formData.is_collection_required ?? ncr.is_collection_required;
  const creditRequired = formData.is_credit_required ?? ncr.is_credit_required;
  const replacementRequired = formData.is_replacement_required ?? ncr.is_replacement_required;

  return (
    <Section title="Reject Details & Action">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Reject Qty</Label>
          <Input
            type="number"
            value={formData.reject_qty || 0}
            onChange={(e) => onUpdateFormData('reject_qty', parseInt(e.target.value))}
            disabled={!canEdit}
          />
        </div>
        <div>
          <Label>Shortage Qty</Label>
          <Input
            type="number"
            value={formData.shortage_qty || 0}
            onChange={(e) => onUpdateFormData('shortage_qty', parseInt(e.target.value))}
            disabled={!canEdit}
          />
        </div>
        <div>
          <Label>Source of Rejection</Label>
          {canEdit ? (
            <select
              value={(formData.source_of_rejection_option as number) ?? ncr.source_of_rejection_option ?? ''}
              onChange={(e) => onUpdateFormData('source_of_rejection_option', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Select...</option>
              {sourcesOfRejection.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          ) : (
            <Input value={ncr.source_of_rejection_option_name || '—'} disabled />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Fault Group</Label>
          {canEdit ? (
            <select
              value={selectedFaultGroup ?? ''}
              onChange={(e) => {
                const val = e.target.value ? parseInt(e.target.value) : null;
                onUpdateFormData('fault_group', val);
                onUpdateFormData('fault_category', null);
              }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Select...</option>
              {faultGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          ) : (
            <Input value={ncr.fault_group_name || '—'} disabled />
          )}
        </div>
        <div>
          <Label>Fault Category</Label>
          {canEdit ? (
            <select
              value={(formData.fault_category as number) ?? ncr.fault_category ?? ''}
              onChange={(e) => onUpdateFormData('fault_category', e.target.value ? parseInt(e.target.value) : null)}
              disabled={!selectedFaultGroup}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">{selectedFaultGroup ? 'Select...' : 'Select fault group first'}</option>
              {filteredCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          ) : (
            <Input value={ncr.fault_category_name || '—'} disabled />
          )}
        </div>
      </div>
      {/* Customer review 11 Sep (item 38): where the fault group /
          category came from — the ERP code the import read — and whether
          that code-to-category mapping is still awaiting sign-off. */}
      {ncr.erp_fault_code && (
        <p className="text-xs text-muted-foreground -mt-2">
          ERP fault code <span className="font-mono font-medium text-foreground">{ncr.erp_fault_code}</span>
          {ncr.erp_fault_mapping_provisional === true && (
            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
              Provisional mapping — awaiting customer sign-off
            </span>
          )}
          {ncr.erp_fault_mapping_provisional === null && !ncr.fault_category && (
            <span className="ml-2">— not mapped to a fault category yet</span>
          )}
        </p>
      )}

      {/* Repeat Issue — snapshotted at creation: same part + fault group
          already raised on a previous claim (customer review 2026-07). */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Repeat Issue:</span>
        {ncr.is_repeat_issue ? (
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">Yes</span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">No</span>
        )}
      </div>

      <div>
        <Label>Description of Non-Conformity</Label>
        <Textarea
          value={formData.defect_description || ''}
          onChange={(e) => onUpdateFormData('defect_description', e.target.value)}
          disabled={!canEdit}
          rows={4}
          maxLength={1000}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {(formData.defect_description || '').length}/1000 characters
        </p>
      </div>

      {/* Stock Containment (customer review 2026-07) */}
      <NCRContainmentStatusCard
        ncr={ncr}
        formData={formData as Record<string, unknown>}
        canEdit={canEdit}
        canConfirm={canConfirmContainment}
        onUpdateFormData={onUpdateFormData}
      />

      {/* Liability — kept inline because it gates the action sub-block */}
      <div className="border-t pt-4 mt-2">
        <h4 className="text-sm font-semibold mb-1">Liability <span className="text-red-500">*</span></h4>
        {canEdit && <p className="text-xs text-muted-foreground mb-3">Please Select</p>}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => canEdit && onUpdateFormData('liability_type', 2)}
            disabled={!canEdit}
            className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
              liabilityType === 2
                ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
            } ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            Customer Liability
          </button>
          <button
            type="button"
            onClick={() => canEdit && onUpdateFormData('liability_type', 1)}
            disabled={!canEdit}
            className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
              liabilityType === 1
                ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400'
            } ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            Supplier Liability
          </button>
        </div>

        {/* Internal handling — Customer Liability only. The NCR is resolved
            in-house (never sent to the supplier) and an internal user is
            assigned to complete the 8D. */}
        {liabilityType === 2 && (
          <div className="mt-4 rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-3 dark:border-amber-700 dark:bg-amber-900/10">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={!!(formData.is_internal ?? ncr.is_internal)}
                onChange={(e) => onUpdateFormData('is_internal', e.target.checked)}
                disabled={!canEdit}
              />
              Handle internally (do not send to supplier)
            </label>
            {(formData.is_internal ?? ncr.is_internal) && (
              <div className="mt-3">
                <Label className="text-xs">Assigned To (internal team)</Label>
                <select
                  value={String(formData.assigned_to ?? ncr.assigned_to ?? '')}
                  onChange={(e) => onUpdateFormData('assigned_to', e.target.value ? Number(e.target.value) : null)}
                  disabled={!canEdit}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Unassigned</option>
                  {customerUsers.map((u) => (
                    <option key={u.id} value={u.user.id}>
                      {`${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {liabilityType > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-muted-foreground mb-3">
              Required actions for the {liabilityType === 1 ? 'supplier' : 'customer'}:
            </p>
            <div className={`grid ${liabilityType === 1 ? 'grid-cols-4' : 'grid-cols-2'} gap-3`}>
              <div>
                <Label className="text-xs">8D Required <span className="text-red-500">*</span></Label>
                <select
                  value={(formData.is_8d_required ?? ncr.is_8d_required) ? 'yes' : ((formData.is_8d_required ?? ncr.is_8d_required) === false ? 'no' : '')}
                  onChange={(e) => onUpdateFormData('is_8d_required', e.target.value === 'yes')}
                  disabled={!canEdit}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Please Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Collection Required <span className="text-red-500">*</span></Label>
                <select
                  value={collectionRequired ? 'yes' : (collectionRequired === false ? 'no' : '')}
                  onChange={(e) => onUpdateFormData('is_collection_required', e.target.value === 'yes')}
                  disabled={!canEdit}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Please Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              {liabilityType === 1 && (
                <div>
                  <Label className="text-xs">Credit Required <span className="text-red-500">*</span></Label>
                  <select
                    value={creditRequired ? 'yes' : (creditRequired === false ? 'no' : '')}
                    onChange={(e) => onUpdateFormData('is_credit_required', e.target.value === 'yes')}
                    disabled={!canEdit}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Please Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              )}
              {liabilityType === 1 && (
                <div>
                  <Label className="text-xs">Replacement Required <span className="text-red-500">*</span></Label>
                  <select
                    value={replacementRequired ? 'yes' : (replacementRequired === false ? 'no' : '')}
                    onChange={(e) => onUpdateFormData('is_replacement_required', e.target.value === 'yes')}
                    disabled={!canEdit}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Please Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              )}
            </div>

            {/* Customer-liability claims always expose Disposition, even when collection is not required */}
            {(collectionRequired || creditRequired || replacementRequired || liabilityType === 2) && (
              <div className="mt-4 pt-3 border-t border-dashed space-y-3">
                <p className="text-xs font-semibold text-muted-foreground">
                  Disposition &amp; Collection Details:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Proposed Disposition <span className="text-red-500">*</span></Label>
                    <select
                      value={formData.proposed_rejection_type ?? ncr.proposed_rejection_type ?? 0}
                      onChange={(e) => onUpdateFormData('proposed_rejection_type', parseInt(e.target.value))}
                      disabled={!canEdit}
                      className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    >
                      <option value={0}>Please Select</option>
                      <option value={1}>Return to Supplier</option>
                      <option value={2}>Scrap</option>
                      <option value={3}>Rework</option>
                      <option value={4}>Use As Is</option>
                      <option value={5}>Sort</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Disposition Qty</Label>
                    <Input
                      type="number"
                      value={formData.disposition_quantity ?? ncr.disposition_quantity ?? ''}
                      onChange={(e) => onUpdateFormData('disposition_quantity', e.target.value ? parseInt(e.target.value) : null)}
                      disabled={!canEdit}
                      className="h-9"
                      placeholder="Qty"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Action Required <span className="text-red-500">*</span></Label>
                  <select
                    value={formData.action_required ?? ncr.action_required ?? 0}
                    onChange={(e) => onUpdateFormData('action_required', parseInt(e.target.value))}
                    disabled={!canEdit}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value={0}>None</option>
                    <option value={1}>Containment Only</option>
                    <option value={2}>Corrective Action</option>
                    <option value={3}>Full 8D</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Liability Classification</Label>
                  <Input
                    value={formData.liability_classification ?? ncr.liability_classification ?? ''}
                    onChange={(e) => onUpdateFormData('liability_classification', e.target.value)}
                    disabled={!canEdit}
                    className="h-9"
                    placeholder="e.g. Supplier manufacturing defect"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {(ncr.closed_date || ncr.raised_date) && (
        <div className="border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold mb-3">Key Dates</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {ncr.raised_date && (
              <div>
                <Label className="text-xs text-muted-foreground">Raised Date</Label>
                <p className="font-medium">{formatDate(ncr.raised_date)}</p>
              </div>
            )}
            {ncr.closed_date && (
              <div>
                <Label className="text-xs text-muted-foreground">Closed Date</Label>
                <p className="font-medium">{formatDate(ncr.closed_date)}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}
